"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { fetchUser, deleteUserAccount } from "../../funcs/api";
import { toast } from 'react-toastify';

export default function DeleteAccountPage() {
  const router = useRouter();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [emailConfirmation, setEmailConfirmation] = useState('');

  // Require login and load current user
  useEffect(() => {
    try {
      const token =
        (typeof window !== "undefined" && sessionStorage?.getItem("token")) ||
        (typeof window !== "undefined" && localStorage?.getItem("token"));

      if (!token) {
        router.push("/login");
        return;
      }

      fetchUser()
        .then((u) => {
          setUser(u);
          setLoading(false);
        })
        .catch(() => {
          router.push("/login");
        });
    } catch (e) {
      router.push("/login");
    }
  }, [router]);

  const handleLogout = useCallback(() => {
    try {
      sessionStorage?.removeItem("token");
      localStorage?.removeItem("user");
      localStorage?.removeItem("token");
    } catch (_) {}
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowConfirmModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!user?.id) return;
    
    // Validate email confirmation
    if (emailConfirmation !== user?.email) {
      toast.error('البريد الإلكتروني المدخل غير صحيح. يرجى إدخال البريد الإلكتروني الصحيح.');
      return;
    }
    
    setShowConfirmModal(false);
    
    try {
      setDeleting(true);
      await deleteUserAccount();
      
      // Success: show success message and clear auth
      toast.success('تم حذف حسابك بنجاح');
      handleLogout();
      router.replace("/");
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('not allowed via API')) {
        setError('حذف الحساب غير متاح حالياً. يرجى التواصل مع الدعم الفني لحذف حسابك.');
      } else if (error.response?.status === 403) {
        setError('ليس لديك صلاحية لحذف الحساب. يرجى التواصل مع الدعم الفني.');
      } else {
        setError('فشل في حذف الحساب. يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.');
      }
      setDeleting(false);
    }
  }, [handleLogout, router, user?.id, user?.email, emailConfirmation]);

  const handleCancelDelete = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  if (loading) {
    return (
      <div className="container my-5">
        <div className="mx-auto my-5 bg-white text-center col-auto border rounded-4 py-3 px-4 shadow-sm">
          <div className="spinner-border" role="status" aria-label="جار التحميل..."></div>
          <div className="mt-2">جار التحميل...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8">
          <div className="bg-white border rounded-4 p-4 p-md-5 shadow-sm">
            <h1 className="h4 mb-3">حذف حسابك</h1>
            <p className="text-muted mb-4">
              حذف حسابك إجراء نهائي. قد يتم حذف ملفك الشخصي ومنشوراتك والعناصر المحفوظة والبيانات الشخصية المرتبطة بحسابك ولا يمكن استردادها.
            </p>

            <div className="mb-4">
              <h2 className="h6">قبل الحذف</h2>
              <ul className="mt-2">
                <li>فكر في تحميل أي بيانات تريد الاحتفاظ بها.</li>
                <li>ستفقد الوصول إلى جلساتك وتحدياتك والروابط المحفوظة.</li>
                <li>هذا الإجراء لا يمكن التراجع عنه.</li>
              </ul>
            </div>

            <div className="mb-4">
              <h2 className="h6">تحذير مهم</h2>
              <p className="mt-2 mb-0">
                حذف حسابك إجراء نهائي ولا يمكن التراجع عنه. سيتم حذف ملفك الشخصي وبياناتك ذات الصلة نهائياً.
              </p>
            </div>

            {error && (
              <div className="alert alert-warning" role="alert">
                {error}
              </div>
            )}

            <div className="row gx-2 mt-4">
            <button
                onClick={handleDeleteClick}
                className="col-6 btn btn-outline-secondary"
                disabled={deleting}
              >
                {deleting ? "جار الحذف..." : "احذف حسابي"}
              </button>
             <a href="/account" className="col-6 btn btn-danger">
              إلغاء
             </a>
             

              {/* If the WP REST API supports self-deletion for your role, this is the endpoint used. */}
              {/* {user?.id && (
                <div className="text-muted small">
                  API endpoint used: <code>{`${baseURL}api/wp/v2/users/${user.id}?force=true&reassign=0`}</code>
                  <br />
                  This endpoint requires an authenticated request with HTTP DELETE. If your role does not allow deletion, please contact support.
                </div>
              )} */}
              </div>
              <div className="mt-3">
                <Link href="/contact" className="btn btn-outline-secondary">
                  تحتاج مساعدة؟ تواصل مع الدعم الفني
                </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <div className={`modal fade ${showConfirmModal ? 'show' : ''}`} 
           style={{ display: showConfirmModal ? 'block' : 'none' }} 
           tabIndex="-1" 
           role="dialog"
           aria-labelledby="deleteConfirmModalLabel"
           aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content bg-light border">
            <div className="modal-header border-0">
              <h5 className="modal-title text-danger" id="deleteConfirmModalLabel">
                <i className="bi bi-exclamation-triangle"></i> تأكيد حذف الحساب
              </h5>
              <button type="button" className="btn-close" onClick={handleCancelDelete} aria-label="إغلاق"></button>
            </div>
            <div className="modal-body text-center">
              <div className="mb-3">
                <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
              </div>
              <h6 className="mb-3">هل أنت متأكد تماماً من حذف حسابك؟</h6>
              <p className="text-danger fw-bold mb-3">هذا الإجراء نهائي ولا يمكن التراجع عنه.</p>
              <div className="alert alert-warning mb-4">
                <small>
                  <strong>تحذير:</strong> سيتم حذف جميع بياناتك الشخصية والروابط والمحتوى المرتبط بحسابك نهائياً.
                </small>
              </div>
              
              {/* Email Confirmation */}
              <div className="mb-4">
                <label htmlFor="emailConfirmationModal" className="form-label">
                  <strong>لتأكيد حذف الحساب، يرجى كتابة بريدك الإلكتروني:</strong>
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="emailConfirmationModal"
                  value={emailConfirmation}
                  onChange={(e) => setEmailConfirmation(e.target.value)}
                  placeholder="أدخل بريدك الإلكتروني هنا"
                  disabled={deleting}
                />
                <div className="form-text text-muted">
                  البريد الإلكتروني المرتبط بحسابك: <strong>{user?.email}</strong>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 justify-content-center">
              <button type="button" className="btn btn-outline-secondary" onClick={handleConfirmDelete} disabled={deleting || !emailConfirmation}>
                {deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash"></i> نعم، احذف الحساب
                  </>
                )}
              </button>
              <button type="button" className="btn btn-danger me-2" onClick={handleCancelDelete}>
                <i className="bi bi-x-circle"></i> إلغاء
              </button>
              
            </div>
          </div>
        </div>
      </div>

      {/* Modal Backdrop */}
      {showConfirmModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}


